import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Chip,
  Card,
  CardContent,
  Tooltip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  PushPin as PushPinIcon,
  Article as ArticleIcon,
  Public as PublicIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
// [Web UX 1. csomag (4.3), 2026-10-03] a hír-időbélyegek (`createdDate`, `publishedDate`) UTC-ben, „Z” nélkül jönnek
// (`NewsController.cs:191-192`, élő API-n mérve) → a közös segéd budapesti időben írja ki.
import { formatServerDateTime, formatServerDateLong } from '../utils/dateUtils';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext';
import { isFormDirty, discardChangesConfirm } from '../utils/formDirty';
import { useFeedback } from '../contexts/FeedbackContext';
import { API_BASE_URL } from '../utils/config';
// [Web UX 2. csomag – Belépés és munkamenet, 2026-10-03] A `fetch`-ágak 401-ét semmilyen axios-interceptor
// nem látja (leltár 2(a)): minden hitelesített hívás után a közös jelzőnek adjuk át (`utils/session.ts`).
// [Web 7 / T1, 2026-10-05] a hitelesített `fetch`-ek a közös `authFetch` burkolón mennek: token, csendes
// megújítás, 401 utáni egyszeri ismétlés és a lejárat-jelzés (`reportFetchUnauthorized`) EGY helyen.
import { authFetch } from '../utils/authFetch';
import { requestResponseErrorMessage, saveResponseErrorMessage } from '../utils/saveErrors';

/**
 * A szerver `NewsDto`-ja (backend/PinyoApi/DTOs/NewsDto.cs) — a szerzőt `authorName`-ben küldi.
 * [É3, 2026-09-13] Ez a típus korábban `createdByName`/`createdBy`/`modifiedBy(Name)` mezőket
 * deklarált, amiket a szerver SOHA nem küld, ezért a lista „Létrehozva" oszlopában a szerző
 * neve mindig üres volt (a teszt-fixture is a hibás mezőt adta, ezért zöld maradt).
 */
interface News {
  newsId: string;
  title: string;
  summary: string;
  content: string;
  isPublished: boolean;
  isPinned: boolean;
  publishedDate?: string;
  authorName?: string;
  createdDate: string;
  modifiedDate?: string;
}

interface NewsFormData {
  title: string;
  summary: string;
  content: string;
  isPublished: boolean;
  isPinned: boolean;
}

const EMPTY_NEWS_FORM: NewsFormData = {
  title: '',
  summary: '',
  content: '',
  isPublished: false,
  isPinned: false,
};

const NewsManagement: React.FC = () => {
  const { user } = useAuth();
  const confirm = useConfirm();
  const { showSuccess } = useFeedback();
  const [news, setNews] = useState<News[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * [7. javító kör — validátor F3, 2026-10-04] A hír mentésének hibája a NYITVA maradó dialógusban
   * is látszik. KORÁBBAN csak a lap tetejei sávba került (`error`), ami a modális dialógus MÖGÖTT
   * volt — a „Létrehozás"/„Mentés" után semmilyen visszajelzés nem látszott.
   */
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedNews, setSelectedNews] = useState<News | null>(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [formData, setFormData] = useState<NewsFormData>(EMPTY_NEWS_FORM);
  /**
   * A szerkesztő MEGNYITÁSKORI állapota, mint alapvonal a mentetlen-változás védelemhez.
   * A hír szövege a leghosszabb beírható tartalom a felületen — a bezárásra ezért kérdezünk rá.
   * `null` = a dialógus zárva / még nem nyitottuk meg → soha nem piszkos.
   */
  const [baseline, setBaseline] = useState<NewsFormData | null>(null);

  const isAdmin = user?.userRole === 'Administrator';

  /**
   * A hír SZÖVEGES mezőinek KARAKTER-HŰ összevetése — a közös `isFormDirty` MELLETT
   * [NB-9, 2026-09-14].
   *
   * MIÉRT KELL: a `formDirty` normalizálója TRIM-el (a név köré írt szóköz joggal nem
   * módosítás), a HÍR TARTALMA viszont `white-space: pre-line`/`pre-wrap`-pel jelenik meg
   * (`NewsReader.tsx:97`, `NewsManagement.tsx` előnézet), tehát a szöveg SZÉLÉRE tett üres sor
   * LÁTSZIK — vagyis JELENTÉSE VAN. Trim-elő összevetéssel az ilyen változás „nem piszkos"
   * lett volna, a bezárás pedig kérdés NÉLKÜL eldobta volna.
   *
   * ÍGY A KAPU AZT ŐRZI, AMI VALÓBAN ELMEGY: a mentés 2026-09-14 óta NEM trim-el (lásd
   * `handleSave`), tehát az összevetésnek is karakter-hűnek kell lennie. Ára: egy véletlen záró
   * szóköz a címben is „piszkos"-nak számít — ezt tudatosan vállaljuk, mert a két fél
   * (kapu és mentés) ellentmondása rosszabb volt: az egyik szerint nem volt módosítás, a másik
   * mégis más szöveget írt a szerverre.
   */
  const textContentChanged = (base: NewsFormData | null, form: NewsFormData) =>
    base !== null &&
    (base.title !== form.title || base.summary !== form.summary || base.content !== form.content);

  const isDirty = dialogOpen && (isFormDirty(baseline, formData) || textContentChanged(baseline, formData));
  // A BEJELENTETT NÉV = amit a felhasználó a FEJLÉCBEN lát [N-16, 2026-09-14]: a fül-váltás
  // kérdése ezt a nevet mondja vissza („Mentetlen változásod van itt: …"), ezért nem mondhat
  // „Hír szerkesztése"-t, ha a felhasználó épp „Új hír létrehozása" fejléc alatt áll.
  useUnsavedChanges(isDirty, selectedNews ? 'Hír szerkesztése' : 'Új hír létrehozása');

  useEffect(() => {
    if (isAdmin) {
      fetchNews();
    }
  }, [isAdmin]);

  const fetchNews = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API_BASE_URL}/api/news`);

      // [Web UX 3. csomag (D), 2026-10-04] a `!ok` válasz a szerver magyar indoklását / „<tartalék> (HTTP n)"-t adja (eddig egy
      // angol `throw new Error('Failed to …')` után a catch FIX szövege ment ki, az indoklás elveszett).
      if (!response.ok) {
        const body = await response.text().catch(() => '');
        setError(requestResponseErrorMessage(body, response.status, 'Nem sikerült betölteni a híreket'));
        return;
      }
      
      const data = await response.json();
      setNews(data);
    } catch (err) {
      setError('Nem sikerült betölteni a híreket');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (newsItem?: News) => {
    const initial: NewsFormData = newsItem
      ? {
          title: newsItem.title,
          summary: newsItem.summary,
          content: newsItem.content,
          isPublished: newsItem.isPublished,
          isPinned: newsItem.isPinned,
        }
      : EMPTY_NEWS_FORM;
    setSelectedNews(newsItem ?? null);
    setFormData(initial);
    // Alapvonal a megnyitás pillanatában: a betöltött hír önmagában nem módosítás.
    setBaseline(initial);
    setDialogError(null);
    setDialogOpen(true);
  };

  /** Kérdés NÉLKÜLI zárás — a sikeres mentés használja, ott már nincs mentetlen adat. */
  const closeDialog = () => {
    setDialogError(null);
    setDialogOpen(false);
    setSelectedNews(null);
    setFormData(EMPTY_NEWS_FORM);
    setBaseline(null);
  };

  /** A felhasználó záró útjai (Mégse, backdrop, ESC): beírt adat esetén a közös ablak kérdez. */
  const handleCloseDialog = async () => {
    if (isDirty && !(await confirm(discardChangesConfirm('hír szerkesztője')))) {
      return;
    }
    closeDialog();
  };

  const handleSave = async () => {
    setError(null);
    setDialogError(null);

    /**
     * A SZÖVEG ÚGY MEGY KI, AHOGY A SZERKESZTŐ BEÍRTA — a mentés NEM trim-el
     * [N-13 újradöntve, NB-9, 2026-09-14].
     *
     * A korábbi megoldás a címet/kivonatot/tartalmat `trim()`-elte a mentésben, hogy a
     * trim-elő mentetlen-összevetéssel egy nyelvet beszéljen. A VALIDÁTOR (4. kör) MÉRTE a
     * következményét: a mentés NEM volt piszkos-állapothoz kötve, tehát aki megnyitott egy hírt
     * és VÁLTOZTATÁS NÉLKÜL „Mentés"-t nyomott, az levágta a szöveg elejéről/végéről a
     * SZÁNDÉKOS üres sort — NÉMA adat-módosítás a szerző szövegén. A hír tartalma
     * `white-space: pre-line`-nal jelenik meg (`NewsReader.tsx:97`), tehát a széli üres sor
     * LÁTSZIK: nem „jelentés nélküli whitespace".
     *
     * A DÖNTÉS tehát: a mentés karakter-hű, és a KÉT FÉL ÖSSZHANGJÁT a másik oldalon
     * teremtjük meg — a mentetlen-összevetés (`textContentChanged`) a három szöveges mezőt
     * KARAKTER-HŰEN hasonlítja össze, tehát a széli üres sor hozzáadása/elvétele is „piszkos",
     * és a bezárás rákérdez. Őr-tesztek: `NewsManagement.unsaved.test.tsx`.
     */
    const payload: NewsFormData = { ...formData };

    try {
      const url = selectedNews
        ? `${API_BASE_URL}/api/news/${selectedNews.newsId}`
        : `${API_BASE_URL}/api/news`;
      
      const method = selectedNews ? 'PUT' : 'POST';
      
      const response = await authFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      // A mentés 401-e: a közös jelző az újra-belépő ablakot nyitja (a nyitott, piszkos hír-űrlap
      // miatt nem jelentkeztet ki); az űrlap nyitva marad, a szöveg megmarad.
      // [3. javító kör, F-1] a szöveget a közös, zár-tudatos `saveResponseErrorMessage` adja (a
      // `reportFetchUnauthorized` a saját tokenünk 401-ére állítja a zárat — ma az `authFetch`-en belül,
      // egy sikertelen csendes megújítás után [Web 7 / T1, 2026-10-05]).

      if (!response.ok) {
        const body = await response.text().catch(() => '');
        const message = saveResponseErrorMessage(body, response.status, 'Nem sikerült menteni a hírt');
        setError(message);
        // [7. javító kör — validátor F3] a nyitva maradó dialógusban is (a lap-sáv mögötte van)
        setDialogError(message);
        return;
      }

      showSuccess(selectedNews ? 'A hír módosítva.' : 'A hír létrehozva.');
      closeDialog();
      await fetchNews();
    } catch (err) {
      setError('Nem sikerült menteni a hírt');
      setDialogError('Nem sikerült menteni a hírt');
      console.error(err);
    }
  };

  const handleDeleteClick = async (newsItem: News) => {
    const confirmed = await confirm({
      title: 'Hír törlése',
      message: (
        <>
          Biztosan törölni szeretnéd ezt a hírt: <strong>{newsItem.title}</strong>?
        </>
      ),
      confirmLabel: 'Törlés',
      destructive: true,
    });
    if (!confirmed) return;

    try {
      const response = await authFetch(`${API_BASE_URL}/api/news/${newsItem.newsId}`, {
        method: 'DELETE',
      });

      // [Web UX 3. csomag (D), 2026-10-04] a `!ok` válasz a szerver magyar indoklását / „<tartalék> (HTTP n)"-t adja (eddig egy
      // angol `throw new Error('Failed to …')` után a catch FIX szövege ment ki, az indoklás elveszett).
      if (!response.ok) {
        const body = await response.text().catch(() => '');
        setError(requestResponseErrorMessage(body, response.status, 'Nem sikerült törölni a hírt'));
        return;
      }

      showSuccess('A hír törölve.');
      await fetchNews();
    } catch (err) {
      setError('Nem sikerült törölni a hírt');
      console.error(err);
    }
  };

  const handleTogglePublished = async (newsItem: News) => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/news/${newsItem.newsId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...newsItem,
          isPublished: !newsItem.isPublished,
        }),
      });

      // [Web UX 3. csomag (D), 2026-10-04] a `!ok` válasz a szerver magyar indoklását / „<tartalék> (HTTP n)"-t adja (eddig egy
      // angol `throw new Error('Failed to …')` után a catch FIX szövege ment ki, az indoklás elveszett).
      if (!response.ok) {
        const body = await response.text().catch(() => '');
        setError(requestResponseErrorMessage(body, response.status, 'Nem sikerült frissíteni a hírt'));
        return;
      }

      await fetchNews();
      showSuccess(!newsItem.isPublished ? 'A hír közzétéve.' : 'A hír visszavonva.');
    } catch (err) {
      setError('Nem sikerült frissíteni a hírt');
      console.error(err);
    }
  };

  const handleTogglePinned = async (newsItem: News) => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/news/${newsItem.newsId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...newsItem,
          isPinned: !newsItem.isPinned,
        }),
      });

      // [Web UX 3. csomag (D), 2026-10-04] a `!ok` válasz a szerver magyar indoklását / „<tartalék> (HTTP n)"-t adja (eddig egy
      // angol `throw new Error('Failed to …')` után a catch FIX szövege ment ki, az indoklás elveszett).
      if (!response.ok) {
        const body = await response.text().catch(() => '');
        setError(requestResponseErrorMessage(body, response.status, 'Nem sikerült frissíteni a hírt'));
        return;
      }

      await fetchNews();
      showSuccess(!newsItem.isPinned ? 'A hír kitűzve.' : 'A hír kitűzése megszüntetve.');
    } catch (err) {
      setError('Nem sikerült frissíteni a hírt');
      console.error(err);
    }
  };

  const getStatistics = () => {
    const published = news.filter(n => n.isPublished).length;
    const pinned = news.filter(n => n.isPinned).length;
    const draft = news.filter(n => !n.isPublished).length;
    return { total: news.length, published, pinned, draft };
  };

  const stats = getStatistics();

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Csak adminisztrátorok férhetnek hozzá ehhez az oldalhoz.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Hírek kezelése
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Új hír
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ArticleIcon color="primary" />
                <Typography color="text.secondary" gutterBottom>
                  Összes hír
                </Typography>
              </Box>
              <Typography variant="h4">{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PublicIcon color="success" />
                <Typography color="text.secondary" gutterBottom>
                  Publikált
                </Typography>
              </Box>
              <Typography variant="h4">{stats.published}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PushPinIcon color="warning" />
                <Typography color="text.secondary" gutterBottom>
                  Kitűzött
                </Typography>
              </Box>
              <Typography variant="h4">{stats.pinned}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ScheduleIcon color="action" />
                <Typography color="text.secondary" gutterBottom>
                  Piszkozat
                </Typography>
              </Box>
              <Typography variant="h4">{stats.draft}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alerts — a siker már globális toast (FeedbackContext), itt csak a hiba marad tartósan. */}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* News Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Cím</TableCell>
              <TableCell>Összefoglaló</TableCell>
              <TableCell align="center">Állapot</TableCell>
              <TableCell>Létrehozva</TableCell>
              <TableCell>Publikálva</TableCell>
              <TableCell align="center">Műveletek</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Box sx={{ py: 3 }}>
                    <Typography>Betöltés...</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : news.length === 0 ? (
              // Üres állapot: eddig ez volt az EGYETLEN admin-lista, ahol nincs hír esetén semmi
              // sem jelent meg a fejlécen kívül. [Web UX #8, 2026-09-12]
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Box sx={{ py: 4 }}>
                    <Typography color="text.secondary" gutterBottom>
                      Még nincs hír. Hozd létre az elsőt!
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenDialog()}
                      sx={{ mt: 1 }}
                    >
                      Új hír
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ) : news.map((item) => (
              <TableRow key={item.newsId}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {item.title}
                    {item.isPinned && (
                      <Tooltip title="Kitűzött">
                        <PushPinIcon color="warning" fontSize="small" />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell>{item.summary}</TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    {item.isPublished ? (
                      <Chip label="Publikált" color="success" size="small" />
                    ) : (
                      <Chip label="Piszkozat" color="default" size="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  {formatServerDateTime(item.createdDate)}
                  {item.authorName && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {item.authorName}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {formatServerDateTime(item.publishedDate, { fallback: '-' })}
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <Tooltip title="Előnézet">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedNews(item);
                          setPreviewDialogOpen(true);
                        }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={item.isPublished ? 'Visszavonás' : 'Publikálás'}>
                      <IconButton
                        size="small"
                        onClick={() => handleTogglePublished(item)}
                      >
                        {item.isPublished ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={item.isPinned ? 'Kitűzés megszüntetése' : 'Kitűzés'}>
                      <IconButton
                        size="small"
                        onClick={() => handleTogglePinned(item)}
                        color={item.isPinned ? 'warning' : 'default'}
                      >
                        <PushPinIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Szerkesztés">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(item)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Törlés">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(item)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedNews ? 'Hír szerkesztése' : 'Új hír létrehozása'}
        </DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mt: 1 }} data-testid="news-dialog-error">
              {dialogError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Cím"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Összefoglaló"
              value={formData.summary}
              onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
              fullWidth
              multiline
              rows={2}
              required
              helperText="Rövid összefoglaló, ami megjelenik a listában"
            />
            <TextField
              label="Tartalom"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              fullWidth
              multiline
              rows={6}
              required
              helperText="Teljes hír tartalma"
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isPublished}
                    onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
                  />
                }
                label="Publikálás"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isPinned}
                    onChange={(e) => setFormData({ ...formData, isPinned: e.target.checked })}
                  />
                }
                label="Kitűzés"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Mégse</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.title || !formData.summary || !formData.content}
          >
            {selectedNews ? 'Mentés' : 'Létrehozás'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Hír előnézet
            {selectedNews?.isPinned && <PushPinIcon color="warning" />}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedNews && (
            <Box>
              <Typography variant="h5" gutterBottom>
                {selectedNews.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {formatServerDateLong(selectedNews.publishedDate, { withTime: true, fallback: 'Még nem publikált' })}
              </Typography>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                {selectedNews.summary}
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {selectedNews.content}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Bezárás</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default NewsManagement;